import { useEffect } from 'react';

import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';

import { useAuth } from '#globalContexts/AuthProvider';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import AppShell from '#layouts/AppShell';
import { ADDON_CONFIGS } from '#lib/addons/addonConfigs';

/**
 * If a required capability is an add-on's `<addonKey>.access` and that add-on is known,
 * returns the addonKey so the not-allowed page can show that add-on's upsell. Else null.
 * @param {string|string[]|undefined} required
 * @returns {string|null}
 */
function gatedAddonKey(required) {
	const caps = Array.isArray(required) ? required : [required].filter(Boolean);
	for (const cap of caps) {
		const key = typeof cap === 'string' && cap.endsWith('.access') ? cap.slice(0, -'.access'.length) : null;
		if (key && ADDON_CONFIGS[key]) return key;
	}
	return null;
}

/**
 * Combined wrapper: ProtectedRoute + AppShell.
 * Renders Outlet inside AppShell only if the user is authenticated and meets the optional
 * capability requirement.
 */
export default function ProtectedAppRoute({ required }) {
	const { user, loading: authLoading } = useAuth();
	const { hasCapability, isLoading: capLoading } = useCapabilityCheck(required);
	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		if (authLoading || capLoading) return;

		if (!user) {
			navigate('/login', { state: { from: location }, replace: true });
		}
	}, [user, authLoading, capLoading, location, navigate]);

	if (authLoading || capLoading) return null;
	if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
	if (!hasCapability) {
		// Surface an add-on-specific upsell on the not-allowed page when an add-on gate failed.
		const addonKey = gatedAddonKey(required);
		return <Navigate to="/not-allowed" state={addonKey ? { addon: addonKey } : undefined} replace />;
	}

	return <AppShell />;
}

ProtectedAppRoute.propTypes = {
	required: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};
